import { InputRichText } from '@dixa/lib-shared-habla';
import { Channel } from 'lib-shared-types-channels';
import { memo, useMemo } from 'react';

import { CopilotAction, CopilotDropdown } from 'components/copilotDropdown';
import { useCopilotTrial } from 'utils/useCopilotTrial';

import { CustomAiDraftContent } from './components/customAiDraft';
import { EditorCopilotDropdownQuickAiDraft } from './components/quickAiDraft';
import { EditorCopilotDropdownContext } from './editorCopilotDropdownContext';

export const DEFAULT_PINNED_STATE = {
  improveReply: false,
  improveReplyMatchStyleGuide: false,
  checkSpellingAndGrammar: false,
  translateToConversationLanguage: true,
  expandText: true,
  customAiDraft: false,
  quickAiDraft: true,
};

interface EditorCopilotDropdownProps {
  editorCopilotActions: Record<string, CopilotAction>;
  channel?: Channel;
  dixaConversationId?: string;
}

/**
 * Represents a dropdown component designed to be integrated into a rich text
 * editor's toolbar as an addon, providing AI-powered functionalities to enhance
 * and refine the content within the editor.
 */
export const EditorCopilotDropdown = memo(function EditorCopilotDropdown({
  editorCopilotActions,
  channel,
  dixaConversationId,
}: EditorCopilotDropdownProps): JSX.Element | null {
  const { hasCopilot } = useCopilotTrial();

  const actions = useMemo<Record<string, CopilotAction>>(() => {
    if (!dixaConversationId) return editorCopilotActions;

    const customAiDraftAction: CopilotAction = {
      title: 'Custom Mim drafts',
      icon: 'mim',
      hasDividerBefore: false,
      commandName: 'richTextEditor:customAiDraft',
      isVisible: ({ featureFlags }) => featureFlags?.mimDrafts ?? false,
      isPinned: ({ pinnedState }) =>
        pinnedState?.customAiDraft ?? DEFAULT_PINNED_STATE.customAiDraft,
      content: <CustomAiDraftContent dixaConversationId={dixaConversationId} />,
    };

    const quickAiDraftAction: CopilotAction = {
      title: 'Quick Mim drafts',
      icon: 'mim',
      hasDividerBefore: false,
      commandName: 'richTextEditor:quickAiDraft',
      isVisible: ({ featureFlags }) => featureFlags?.mimDrafts ?? false,
      isPinned: ({ pinnedState }) =>
        pinnedState?.quickAiDraft ?? DEFAULT_PINNED_STATE.quickAiDraft,
      content: (
        <EditorCopilotDropdownQuickAiDraft
          dixaConversationId={dixaConversationId}
        />
      ),
    };

    const entries = Object.entries(editorCopilotActions);
    return Object.fromEntries([
      ...entries.slice(0, -1),
      ['quickAiDraft', quickAiDraftAction],
      ['customAiDraft', customAiDraftAction],
      ...entries.slice(-1),
    ]);
  }, [editorCopilotActions, dixaConversationId]);

  if (!hasCopilot) {
    return null;
  }

  return (
    <EditorCopilotDropdownContext.Provider value={{ channel }}>
      <div>
        <CopilotDropdown actions={actions} fixed noArrow noStyle>
          <InputRichText.ToolbarButton icon="ai" />
        </CopilotDropdown>
      </div>
    </EditorCopilotDropdownContext.Provider>
  );
});
